import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import {
    addCommentToProduct,
    updateCommentInProduct,
} from '@services/adminAddCommentToProductFirebaseService';
import style from './ClientReviewModal.module.css';
import modalClose from '@/ui/assets/icons/modalClose.svg';
import {ASSETS} from "@config/assetsConfig";

const AVATARS = ASSETS.avatars;

const findAvatarIndex = (avatarUrl) => {
    if (!avatarUrl) return 0;
    const url = typeof avatarUrl === 'object' ? avatarUrl.path : String(avatarUrl);
    const idx = AVATARS.findIndex(
        (av) => av === url || String(av).endsWith(url) || url.includes(String(av))
    );
    return idx >= 0 ? idx : 0;
};

const ReviewModal = ({ onClose, productId, reviewerId, existingComment, onCommentSaved }) => {
    const isEditMode = Boolean(existingComment);
    const [selectedAvatar, setSelectedAvatar] = useState(0);
    const [name, setName] = useState('');
    const [comment, setComment] = useState('');
    const [rating, setRating] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!existingComment) return;

        setName(existingComment.user_name || '');
        setComment(existingComment.comment || '');
        setRating(Number(existingComment.stars_given) || 0);
        setSelectedAvatar(findAvatarIndex(existingComment.avatar_url));
    }, [existingComment]);

    const handleSubmit = async () => {
        if (!name || !comment || rating === 0) return;
        if (!productId) {
            console.error("ReviewModal: productId no definido");
            alert("No se pudo identificar el producto. Vuelve al catálogo e inténtalo de nuevo.");
            return;
        }

        if (!reviewerId) {
            alert("No se pudo identificar tu sesión. Recarga la página e inténtalo de nuevo.");
            return;
        }

        setLoading(true);
        try {
            const selectedAvatarPath = AVATARS[selectedAvatar] || "";
            const starsGiven = Number(rating);

            const payload = {
                name,
                comment,
                rating: starsGiven,
                avatar: selectedAvatarPath,
                userId: reviewerId,
            };

            if (isEditMode) {
                await updateCommentInProduct(productId, reviewerId, payload);
            } else {
                await addCommentToProduct(productId, payload);
            }

            const now = new Date();

            const savedComment = {
                user_id: reviewerId,
                user_name: name,
                avatar_url: selectedAvatarPath,
                comment,
                stars_given: starsGiven,
                created_at: existingComment?.created_at ?? now,
                ...(isEditMode && { updated_at: now }),
            };

            onCommentSaved?.(savedComment, isEditMode);
            onClose();
        } catch (error) {
            console.error("Error detailed log:", error);
            alert(error.message || "No se pudo guardar tu reseña. Inténtalo de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={style.modalOverlay} onClick={onClose}>
            <div className={style.modalBox} onClick={(e) => e.stopPropagation()}>
                
                <button className={style.modalClose} onClick={onClose}>
                    <img src={modalClose} alt='Cerrar'/>
                </button>

                <p className={style.modalTitle}>
                    {isEditMode ? 'Edita tu reseña' : 'Deja tu reseña'}
                </p>
                <p className={style.modalSubtitle}>Elige tu avatar</p>

                <div className={style.avatarSelector}>
                    {AVATARS.map((av, i) => (
                        <div
                            key={i}
                            className={`${style.avatarOption} ${selectedAvatar === i ? style.selected : ''}`}
                            onClick={() => setSelectedAvatar(i)}
                        >
                            <img src={av} alt={`avatar-${i}`} className={style.avatarImg} />
                        </div>
                    ))}
                </div>

                <input
                    className={style.modalInput}
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                />

                <textarea
                    className={style.modalTextarea}
                    placeholder="Escribe tu comentario"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={loading}
                />

                <span className={style.modalLabel}>Selecciona tu puntuación</span>

                <div className={style.modalStars}>
                    {Array.from({ length: 5 }, (_, i) => (
                        <Star
                            key={i}
                            size={28}
                            fill={i < rating ? '#FFB84D' : 'none'}
                            color={i < rating ? '#FFB84D' : '#ccc'}
                            strokeWidth={1.5}
                            onClick={() => !loading && setRating(i + 1)}
                            style={{ cursor: loading ? 'default' : 'pointer' }}
                        />
                    ))}
                </div>

                <button
                    className={style.modalSubmit}
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading
                        ? "Guardando..."
                        : isEditMode
                            ? "Editar comentario"
                            : "Enviar reseña"}
                </button>
            </div>
        </div>
    );
};

export default ReviewModal;
